import { Fragment, useEffect, useState } from "react";
import { useLocation, useParams } from "react-router-dom";
import parse from "html-react-parser";

import {
  BidDetailType,
  BidKeyword,
  BidKeywordDetail,
  BidKeywordDetailHighlighted,
  BidKeywordDetailKeywords,
} from "@/types/bid";

import { BidTypeCd, ProjectType } from "@/consts/Bid";

import { GetBidDetail, GetBidKeywordDetail } from "@/services/Bid";

import { calculateDDay, cn, getDate, isNotEmpty } from "@/libs/utils";

import CommonImg from "@/components/Common/Img";
import CommonButton from "@/components/Common/Button";
import CommonSelect from "@/components/Common/Select";
import DetailCard from "@/components/DetailCard";
import DetailCardItem from "@/components/DetailCardItem";
import DetailCardItemWrapper from "@/components/DetailCardItemWrapper";
import DetailCardWrapper from "@/components/DetailCardWrapper";
import ItemBadge from "@/components/ItemBadge";
import BidDetailHeader from "@/components/BidDetailHeader";

import ProjectInfoIcon from "@/assets/images/BidDetail/project-info-icon.png";
import AiProjectSummaryIcon from "@/assets/images/BidDetail/ai-project-summary-icon.png";
import AiProductSummaryIcon from "@/assets/images/BidDetail/ai-product-summary-icon.png";
import BidConditionIcon from "@/assets/images/BidDetail/bid-condition-icon.png";
import BusinessRestrictIcon from "@/assets/images/BidDetail/business-restrict-icon.png";
import BidRightIcon from "@/assets/images/BidDetail/bid-right-icon.png";
import KeywordAnalyticsIcon from "@/assets/images/BidDetail/keyword-analytics-icon.png";
import PositiveIcon from "@/assets/images/positive-icon.png";
import NegativeIcon from "@/assets/images/negative-icon.png";
import DocumentIcon from "@/assets/images/BidDetail/document-icon.png";
import CreateProjectDialog from "./components/CreateProjectDialog";
import RedirectLinkedProject from "./components/RedirectLinkedProject";

export default function BidDetail() {
  const { state } = useLocation();
  const keywords = state ? state.keywords : null;
  const { projectType, projectId } = useParams();
  const keywordFilter = keywords?.map((keyword: BidKeyword) => ({
    label: keyword.keyword,
    value: keyword.keyword,
  }));

  const [bidDetail, setBidDetail] = useState<BidDetailType>(
    {} as BidDetailType
  );
  const [isDetailError, setIsDetailError] = useState(false);
  const [selectedKeyword, setSelectedKeyword] = useState<string>("ALL");
  const [selectedKeywordDetail, setSelectedKeywordDetail] = useState<
    BidKeywordDetailKeywords[]
  >([]);
  const [keywordDetail, setKeywordDetail] = useState<
    BidKeywordDetailKeywords[]
  >([]);

  // 레이아웃/헤더는 URL 토큰이 아니라 조회된 공고의 실제 타입을 우선 사용
  const effectiveProjectType = bidDetail?.project_type ?? projectType;
  const isOriginal = () => effectiveProjectType === ProjectType.Original;
  const getBidDetail = async () => {
    if (isNotEmpty(projectId)) {
      try {
        const result = await GetBidDetail(projectId);
        const item = result.data.pods[0]?.content?.items?.[0];
        if (item) {
          setBidDetail(item);
          setIsDetailError(false);
        } else {
          setIsDetailError(true);
        }
      } catch (e) {
        setIsDetailError(true);
      } finally {
      }
    }
  };
  const getBidKeywordDetail = async () => {
    if (isNotEmpty(keywords)) {
      try {
        const keywordResult = await GetBidKeywordDetail(
          projectId,
          keywords.map((keyword: any) => keyword.keyword)
        );
        if (isNotEmpty(keywordResult.data.pods[0].content.keywords)) {
          setKeywordDetail(keywordResult.data.pods[0].content.keywords);
          setSelectedKeywordDetail(keywordResult.data.pods[0].content.keywords);
        }
      } catch (e) {
      } finally {
      }
    }
  };
  const handleChangeKeywordAnalytics = (keyword: string) => {
    setSelectedKeyword(keyword);
    switch (keyword) {
      case "ALL":
        setSelectedKeywordDetail(keywordDetail);
        break;
      default:
        setSelectedKeywordDetail(
          keywordDetail.filter((detail) => detail.keyword === keyword)
        );
        break;
    }
  };

  const handleClickFileDownload = (doc: BidKeywordDetail) => {
    const element = document.createElement("a");
    document.body.appendChild(element);
    element.setAttribute("href", doc.url);
    element.setAttribute("download", doc.filename);
    element.click();
    document.body.removeChild(element);
  };

  const getSelectedKeywordDetail = () => (
    <div className="flex flex-col gap-[20px]">
      {selectedKeywordDetail.map(
        ({ documents, keyword, occurrence }, documentsIndex) => (
          <div key={documentsIndex} className="flex flex-col gap-[20px]">
            <div>
              <CommonButton
                label={`${keyword} ${occurrence ?? "0"}회`}
                className="h-[30px] flex items-center bg-dark-red rounded-[24px] cursor-default"
              />
            </div>
            {documents.map((doc, docIndex) => (
              <div key={docIndex} className="flex flex-col gap-[12px]">
                <div
                  className="text-dark-red text-sm font-semibold underline cursor-pointer"
                  onClick={() => {
                    handleClickFileDownload(doc);
                  }}
                >
                  {doc.filename}
                </div>
                {doc.highlighted_texts.map(
                  (text: BidKeywordDetailHighlighted, textIndex: number) => (
                    <div
                      key={textIndex}
                      className="flex flex-col gap-[12px] text-gray-A text-sm font-normal"
                    >
                      {text.snippets.map(
                        (snippet: string, snippetIndex: number) => {
                          return (
                            <span key={snippetIndex}>
                              {snippetIndex + 1}. {parse(snippet)}
                            </span>
                          );
                        }
                      )}
                    </div>
                  )
                )}
              </div>
            ))}
          </div>
        )
      )}
    </div>
  );
  const onLoad = () => {
    getBidDetail();
    getBidKeywordDetail();
  };

  useEffect(() => {
    onLoad();
  }, []);
  return (
    <BidDetailHeader
      projectType={effectiveProjectType ?? ""}
      projectId={bidDetail?.project_id}
      projectTitle={bidDetail?.title}
      isStarred={bidDetail?.is_starred}
      bidLink={bidDetail?.original_url}
      onRefresh={getBidDetail}
    >
      {isDetailError && (
        <div className="h-64 flex justify-center items-center bg-white rounded-xl border-[1px] border-gray-D">
          <p className="text-lg text-gray-B font-semibold">
            공고 정보를 불러올 수 없습니다
          </p>
        </div>
      )}
      {bidDetail?.project_id && (
        <DetailCardWrapper>
          <DetailCard
            icon={ProjectInfoIcon}
            iconAlt="Project Info"
            title="프로젝트 정보"
            cardClassName={{
              "col-span-2": bidDetail.summary && bidDetail.spec_summary,
              "col-span-3": bidDetail.summary || bidDetail.spec_summary,
              "col-span-6": !bidDetail.summary && !bidDetail.spec_summary,
            }}
          >
            <DetailCardItemWrapper>
              <DetailCardItem label="공고 번호" value={bidDetail.notice_no} />
              <DetailCardItem
                label="공고 유형"
                value={BidTypeCd[bidDetail.category]}
              />
              <DetailCardItem label="수요 기관" value={bidDetail.inst_nm} />
              <DetailCardItem
                label="국제입찰 여부"
                value={
                  bidDetail.international_bid === "Y" ? (
                    <ItemBadge
                      className="text-red bg-red/[.2]"
                      value="국제입찰"
                    />
                  ) : (
                    <ItemBadge
                      className="text-dark-blue bg-dark-blue/[.2]"
                      value="국내입찰"
                    />
                  )
                }
              />
              {isOriginal() ? (
                <>
                  <DetailCardItem
                    label="배정예산"
                    value={
                      bidDetail.budget_amount
                        ? `${Number(bidDetail.budget_amount).toLocaleString()}원`
                        : "공고서 참조"
                    }
                  />
                  <DetailCardItem
                    label="기초금액"
                    value={
                      isNotEmpty(bidDetail.basis_amount)
                        ? `${Number(bidDetail.basis_amount).toLocaleString()}원`
                        : "기초금액 정보 없음"
                    }
                  />
                </>
              ) : (
                <DetailCardItem
                  label="금액"
                  value={`${Number(bidDetail.budget_amount).toLocaleString()}원`}
                />
              )}
              <DetailCardItem
                label="시작일"
                value={getDate(bidDetail.begin_dt)}
              />
              <DetailCardItem
                label="마감일"
                value={
                  <span
                    className={cn({
                      "text-red font-bold":
                        calculateDDay(bidDetail.close_dt) <= 3 &&
                        calculateDDay(bidDetail.close_dt) >= 0, // D-3일 이하일 때 빨간색 강조
                    })}
                  >
                    {getDate(bidDetail.close_dt)}
                  </span>
                }
              />
            </DetailCardItemWrapper>
          </DetailCard>
          <div className="absolute right-0 -top-12 flex items-center justify-end gap-4">
            <RedirectLinkedProject />
            <CreateProjectDialog bidTitle={bidDetail?.title} />
          </div>
          {/* TODO:: DetailCard 확인 필요*/}
          {bidDetail.summary && (
            <DetailCard
              icon={AiProjectSummaryIcon}
              cardClassName={
                bidDetail.spec_summary ? "col-span-2" : "col-span-3"
              }
              iconAlt="AI Project Summary"
              title="AI 프로젝트 요약"
            >
              {bidDetail.summary}
            </DetailCard>
          )}
          {/* TODO:: DetailCard 확인 필요*/}
          {bidDetail.spec_summary && (
            <DetailCard
              icon={AiProductSummaryIcon}
              cardClassName={bidDetail.summary ? "col-span-2" : "col-span-3"}
              iconAlt="AI Product Summary"
              title="AI 물품 요약"
            >
              {bidDetail.spec_summary}
            </DetailCard>
          )}
          <DetailCard
            icon={BidConditionIcon}
            iconAlt="Bid Condition"
            title="공동수급 ・ 지역제한 ・ 선정방식"
          >
            <DetailCardItemWrapper>
              <DetailCardItem label="공동수급" value={bidDetail.joint_method} />
              <DetailCardItem
                label="지역제한"
                value={
                  bidDetail.region_list.length > 0 &&
                  bidDetail.region_list[0].regions
                    .map((region: any) => region.label)
                    .join(", ")
                }
              />
              <DetailCardItem label="제출 방법" value={bidDetail.dir_sub_msg} />
              <DetailCardItem
                label="제출 확약서"
                value={bidDetail.sub_summary}
              />
              <DetailCardItem
                label="기업 제한"
                value={bidDetail.enterprise_info}
              />
              <DetailCardItem
                label="실적 제한"
                value={bidDetail.performance_info}
              />
              <DetailCardItem label="인적 제한" value={bidDetail.expert_info} />
            </DetailCardItemWrapper>
          </DetailCard>
          <DetailCard
            icon={BusinessRestrictIcon}
            iconAlt="Business Restrict"
            title="업종제한"
          >
            {isNotEmpty(bidDetail.license_list) ? (
              <div className="flex flex-col gap-[10px]">
                <div className="flex justify-between text-sm text-gray-A font-normal">
                  <div>입찰 요구 인증서</div>
                  <div>충족여부</div>
                </div>
                <div className="h-[150px] max-h-[150px] flex flex-col gap-[10px]">
                  {bidDetail.license_list?.map(
                    (licenseList, licenseListIndex) => (
                      <Fragment key={licenseListIndex}>
                        <div className="flex justify-start">
                          <div className="w-full">
                            <div className="flex flex-wrap items-center gap-[4px] text-[10px] text-gray-B">
                              {licenseList.licenses.map(
                                (license, licenseIndex) => (
                                  <Fragment key={licenseIndex}>
                                    <ItemBadge
                                      className={{
                                        "text-green bg-green/[.2]":
                                          license.eligible,
                                        "text-pink bg-pink/[.2]":
                                          !license.eligible,
                                      }}
                                      value={`${license.label}(${license.code})`}
                                    />
                                    {licenseIndex <
                                    licenseList.licenses.length - 1
                                      ? "과"
                                      : licenseListIndex ===
                                          bidDetail.license_list.length - 1
                                        ? ""
                                        : "또는"}
                                  </Fragment>
                                )
                              )}
                            </div>
                          </div>
                          <div className="w-[42px] flex justify-center">
                            <CommonImg
                              src={
                                licenseList.eligible
                                  ? PositiveIcon
                                  : NegativeIcon
                              }
                              alt={
                                licenseList.eligible ? "Positive" : "Negative"
                              }
                            />
                          </div>
                        </div>
                        {licenseListIndex <
                          bidDetail.license_list.length - 1 && <hr />}
                      </Fragment>
                    )
                  )}
                </div>
              </div>
            ) : (
              <div className="text-sm text-gray-A font-normal">
                결과가 없습니다.
              </div>
            )}
          </DetailCard>
          <DetailCard
            icon={BusinessRestrictIcon}
            iconAlt="Business Restrict"
            title={`직접생산 분석 [${bidDetail.product_condition}]`}
          >
            {isNotEmpty(bidDetail.product_list) ? (
              <div className="flex flex-col gap-[10px]">
                <div className="flex justify-between text-sm text-gray-A font-normal">
                  <div>입찰 요구 직접생산</div>
                  <div>충족여부</div>
                </div>
                <div className="h-[150px] max-h-[150px] flex flex-col gap-[10px]">
                  {bidDetail.product_list?.map(
                    (productList, productListIndex) => (
                      <Fragment key={productListIndex}>
                        <div className="flex justify-start">
                          <div className="w-full">
                            <div className="flex flex-wrap items-center gap-[4px] text-[10px] text-gray-B">
                              {productList.products.map(
                                (product, productIndex) => (
                                  <Fragment key={productIndex}>
                                    <ItemBadge
                                      className={{
                                        "text-green bg-green/[.2]":
                                          product.eligible,
                                        "text-pink bg-pink/[.2]":
                                          !product.eligible,
                                      }}
                                      value={`${product.label}(${product.code})`}
                                    />
                                    {productIndex <
                                    productList.products.length - 1
                                      ? "과"
                                      : productListIndex ===
                                          bidDetail.product_list.length - 1
                                        ? ""
                                        : "또는"}
                                  </Fragment>
                                )
                              )}
                            </div>
                          </div>
                          <div className="w-[42px] flex justify-center">
                            <CommonImg
                              src={
                                productList.eligible
                                  ? PositiveIcon
                                  : NegativeIcon
                              }
                              alt={
                                productList.eligible ? "Positive" : "Negative"
                              }
                            />
                          </div>
                        </div>
                        {productListIndex <
                          bidDetail.product_list.length - 1 && <hr />}
                      </Fragment>
                    )
                  )}
                </div>
              </div>
            ) : (
              <div className="text-sm text-gray-A font-normal">
                결과가 없습니다.
              </div>
            )}
          </DetailCard>
          {isOriginal() ? (
            <div className="col-span-6 flex flex-col">
              <div className="bg-white flex gap-2 items-center border-[1px] border-gray-D rounded-t-xl p-5">
                <CommonImg src={BidRightIcon} alt="Bid Right" />
                <div className="w-full text-sm text-gray-A font-semibold">
                  입찰 참가 자격
                </div>
              </div>
              <div className="h-[280px] max-h-[280px] overflow-auto bg-white border-[1px] border-t-0 border-gray-D rounded-b-xl p-5 flex gap-[20px]">
                <div className="text-sm text-gray-A font-normal whitespace-pre-wrap">
                  {bidDetail.participation_condition ??
                    "이 공고는 제한이 없는 공고입니다."}
                </div>
              </div>
            </div>
          ) : (
            <div className="col-span-6 flex flex-col">
              <div className="bg-white flex gap-2 items-center border-[1px] border-gray-D rounded-t-xl p-5">
                <CommonImg src={DocumentIcon} alt="Keyword Analytics" />
                <div className="w-full text-sm text-gray-A font-semibold flex justify-between items-center">
                  첨부파일
                </div>
              </div>
              <div className="bg-white border-[1px] border-t-0 border-gray-D rounded-b-xl p-5">
                {bidDetail.documents.length > 0 ? (
                  <div className="flex flex-col gap-[20px]">
                    {bidDetail.documents.map((doc, docIndex) => (
                      <div key={docIndex} className="flex flex-col gap-[12px]">
                        <div
                          className="text-dark-red text-sm font-semibold underline cursor-pointer"
                          onClick={() => {
                            const element = document.createElement("a");
                            document.body.appendChild(element);
                            element.setAttribute("href", doc.url);
                            element.setAttribute("download", doc.filename);
                            element.click();
                            document.body.removeChild(element);
                          }}
                        >
                          {doc.filename}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="h-[240px] text-sm text-gray-A font-normal">
                    결과가 없습니다.
                  </div>
                )}
              </div>
            </div>
          )}
          {isNotEmpty(keywords) && (
            <DetailCard
              icon={KeywordAnalyticsIcon}
              iconAlt="Keyword Analytics"
              title="키워드 분석"
              cardClassName="col-span-6"
              childrenClassName="h-auto"
              titleChildren={
                <CommonSelect
                  className="h-[22px]"
                  defaultValue={selectedKeyword}
                  optionList={keywordFilter}
                  onChange={handleChangeKeywordAnalytics}
                />
              }
            >
              {getSelectedKeywordDetail()}
            </DetailCard>
          )}
          {isOriginal() && (
            <DetailCard
              icon={DocumentIcon}
              iconAlt="Keyword Analytics"
              title="첨부파일"
              cardClassName="col-span-6"
              childrenClassName="h-auto"
            >
              <div className="flex flex-col gap-[20px]">
                {bidDetail.documents.map((doc, docIndex) => (
                  <div key={docIndex} className="flex flex-col gap-[12px]">
                    <div
                      className="text-dark-red text-sm font-semibold underline cursor-pointer"
                      onClick={() => {
                        const element = document.createElement("a");
                        document.body.appendChild(element);
                        element.setAttribute("href", doc.url);
                        element.setAttribute("download", doc.filename);
                        element.click();
                        document.body.removeChild(element);
                      }}
                    >
                      {doc.filename}
                    </div>
                  </div>
                ))}
              </div>
            </DetailCard>
          )}
        </DetailCardWrapper>
      )}
    </BidDetailHeader>
  );
}
